import React, { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Activity, Crown, Target, ScanLine, Zap, X, Mic, Repeat, Film, Share2, Loader2 } from "lucide-react";
import { AnalysisStation, StatusLight, TelemetryTicker, SoundToggle, GOLD, CRIMSON } from "./engine/AnalysisStation";
import { FreezeHud, GlitchOverlay, LockReticle } from "./engine/FreezeHud";
import { ProRadar, ProTrend } from "./engine/ProScoutCharts";
import { useFreezeCycle } from "./engine/useFreezeCycle";
import { CoachVoiceBrief } from "./engine/CoachVoiceBrief";
import { exportFreezeFrame } from "./engine/shareFreezeFrame";
import { ElitePlayerCard } from "./engine/ElitePlayerCard";
import { TechnicalMarkers } from "./engine/TechnicalMarkers";
import { preloadPose, detectPose } from "./engine/pose";
import { playStartup } from "./engine/sound";

const fmtTime = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
const efCenter = (ef) => ({ x: ef.cx != null ? ef.cx : ef.x + ef.w / 2, y: ef.cy != null ? ef.cy : ef.y + ef.h / 2 });

// Map an Engine-normalized point onto the ACTUAL rendered video rectangle (object-contain letterbox aware).
const mapToVideo = (v, nx, ny) => {
  if (!v || !v.videoWidth || !v.videoHeight || !v.clientWidth || !v.clientHeight) return { x: nx * 100, y: ny * 100 };
  const cw = v.clientWidth, ch = v.clientHeight, vw = v.videoWidth, vh = v.videoHeight;
  const scale = Math.min(cw / vw, ch / vh);
  const dw = vw * scale, dh = vh * scale;
  const ox = (cw - dw) / 2, oy = (ch - dh) / 2;
  return { x: ((ox + nx * dw) / cw) * 100, y: ((oy + ny * dh) / ch) * 100 };
};

// ---- Loading HUD: "Elite Technical Scan in Progress" ----
export const EliteScan = ({ label }) => (
  <div data-testid="elite-scan" className="relative w-full aspect-video rounded-lg overflow-hidden border border-[#D4AF37]/40 bg-black">
    <div className="absolute inset-0 opacity-40" style={{
      backgroundImage: "linear-gradient(rgba(245,197,24,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(245,197,24,0.16) 1px, transparent 1px)",
      backgroundSize: "26px 26px",
    }} />
    <motion.div className="absolute left-0 right-0 h-14 pointer-events-none"
      style={{ background: "linear-gradient(180deg, transparent, rgba(245,197,24,0.4), transparent)", boxShadow: "0 0 30px rgba(245,197,24,0.55)" }}
      initial={{ top: "-15%" }} animate={{ top: ["-15%", "100%"] }} transition={{ duration: 1.7, repeat: Infinity, ease: "linear" }} />
    {["top-2 left-2 border-t-2 border-l-2", "top-2 right-2 border-t-2 border-r-2", "bottom-2 left-2 border-b-2 border-l-2", "bottom-2 right-2 border-b-2 border-r-2"].map((c) => (
      <div key={c} className={`absolute w-6 h-6 border-[#F5C518]/70 ${c}`} />
    ))}
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center">
      <ScanLine size={30} className="text-[#F5C518] animate-pulse" />
      <p className="font-mono text-[#F5C518] text-xs sm:text-sm tracking-[0.32em] uppercase">Elite Technical Scan</p>
      <p className="font-mono text-white/55 text-[10px] tracking-[0.28em] uppercase">In Progress…</p>
      {label && <p className="font-mono text-white/35 text-[10px] tracking-widest uppercase mt-1">{label}</p>}
    </div>
    <div className="absolute top-2 left-2 flex items-center gap-1.5 z-20">
      <StatusLight /><span className="font-mono text-[8px] tracking-[0.3em] uppercase text-[#F5C518]/80">System_Active</span>
    </div>
    <div className="absolute bottom-0 left-0 right-0"><TelemetryTicker engine={null} /></div>
  </div>
);

// ---- Compact summary shown on a proof card ----
export const EngineSummary = ({ proof, onOpen }) => {
  const status = proof.engine_status;
  const e = proof.engine;
  if (status === "scanning" || (!e && status !== "failed")) {
    return (
      <div data-testid={`engine-scanning-${proof.id}`} className="mt-3 rounded-md border border-[#D4AF37]/30 bg-black/40 px-3 py-2.5 flex items-center gap-2">
        <ScanLine size={14} className="text-[#F5C518] animate-pulse" />
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#F5C518]">Elite Scan in progress…</span>
      </div>
    );
  }
  if (status === "failed" || !e) {
    return (
      <div className="mt-3 rounded-md border border-white/10 bg-black/40 px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-white/40">
        Scan unavailable
      </div>
    );
  }
  const axes = [["BAL", e.balance], ["EXE", e.execution], ["PACE", e.pace]];
  return (
    <button data-testid={`engine-summary-${proof.id}`} onClick={onOpen}
      className="mt-3 w-full text-left rounded-md border border-[#D4AF37]/30 bg-black/50 hover:border-[#D4AF37]/70 transition-colors px-3 py-2.5 group">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#F5C518] flex items-center gap-1.5"><Activity size={12} /> Engine Report</span>
        {e.elite ? <span className="font-mono text-[9px] tracking-widest uppercase text-black bg-[#F5C518] rounded-full px-2 py-0.5 flex items-center gap-1"><Crown size={10} /> Elite</span>
          : <span className="font-mono text-[9px] tracking-widest uppercase text-white/40 group-hover:text-white/70">View ▸</span>}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {axes.map(([k, v]) => (
          <div key={k} className="text-center rounded bg-white/[0.03] border border-white/10 py-1.5">
            <div className="font-mono text-lg leading-none" style={{ color: v >= 9 ? GOLD : v >= 6 ? "#fff" : CRIMSON }}>{v}<span className="text-[9px] text-white/35">/10</span></div>
            <div className="font-mono text-[8px] tracking-widest uppercase text-white/40 mt-1">{k}</div>
          </div>
        ))}
      </div>
    </button>
  );
};

// ---- Hero station: slow-mo clip that freezes on the Engine's exact frame and blooms the HUD ----
const FreezeStation = ({ proof, e, token, api, player }) => {
  const fixRef = useRef(null);
  const [sharing, setSharing] = useState(false);
  const ef = e.error_frame;
  const [skeleton, setSkeleton] = useState(null);
  const { frozen, glitch, frozenCenter, fitTick, videoProps, withFreezeFrame } = useFreezeCycle(fixRef, ef, proof.id, (nx, ny) => mapToVideo(fixRef.current, nx, ny));
  void fitTick;
  useEffect(() => { preloadPose().catch(() => {}); }, []);
  // Run pose tracking on the exact frozen frame; the rig is cleared when the clip resumes.
  useEffect(() => {
    if (!frozen) { setSkeleton(null); return; }
    let alive = true;
    detectPose(fixRef.current).then((pts) => { if (alive) setSkeleton(pts); }).catch(() => { if (alive) setSkeleton(null); });
    return () => { alive = false; };
  }, [frozen, proof.id]);
  const skelPct = skeleton ? Object.fromEntries(Object.entries(skeleton).map(([k, p]) => [k, mapToVideo(fixRef.current, p.x, p.y)])) : null;
  const live = ef ? mapToVideo(fixRef.current, efCenter(ef).x, efCenter(ef).y) : { x: 50, y: 50 };
  const center = frozen && frozenCenter ? frozenCenter : live;
  const share = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      await withFreezeFrame(async (v) => {
        const pts = skeleton || (await detectPose(v).catch(() => null));
        await exportFreezeFrame({ video: v, engine: e, playerName: player?.display_name || player?.name, drill: proof.move_label, skeleton: pts });
      });
    } catch (err) { toast.error("Couldn't build the freeze frame image. Try again once the clip has loaded."); }
    finally { setSharing(false); }
  };
  return (
    <div data-testid={`fix-clip-wrap-${proof.id}`} className="space-y-2">
      <div className="flex items-center gap-2">
        <Repeat size={13} className="text-[#F5C518]" />
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60">Slow-Mo · Freeze-Frame Analysis</span>
      </div>
      <AnalysisStation engine={e} mode={frozen ? (skelPct ? "Skeletal Lock" : "Frame Locked") : "Slow-Mo x0.33"} testId={`fix-frame-${proof.id}`}>
        <video ref={fixRef} data-testid={`fix-clip-${proof.id}`} src={`${api}/proofs/fixclip/${proof.id}?auth=${token}`}
          autoPlay muted playsInline {...videoProps}
          style={{ filter: frozen ? "grayscale(0.75) brightness(0.5) contrast(1.15)" : "none", transition: "filter 450ms ease" }}
          className="w-full aspect-video bg-black block object-contain" />
        <AnimatePresence>
          {frozen && <motion.div className="absolute inset-0 bg-black/40 pointer-events-none z-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.45 }} />}
        </AnimatePresence>
        <AnimatePresence>{glitch && <GlitchOverlay />}</AnimatePresence>
        <AnimatePresence>
          {ef && !frozen && (
            <motion.div data-testid={`tracking-reticle-${proof.id}`} className="absolute z-30 pointer-events-none" style={{ left: `${center.x}%`, top: `${center.y}%` }}
              initial={{ opacity: 0 }} animate={{ opacity: 0.8 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
              <LockReticle label="◎ Tracking" tracking below={center.y < 18} />
            </motion.div>
          )}
        </AnimatePresence>
        {ef && <FreezeHud cx={center.x} cy={center.y} engine={e} joint={ef.joint} t={ef.t} active={frozen} skeleton={skelPct} testId={`freeze-hud-${proof.id}`} />}
      </AnalysisStation>
      {(ef?.cue || ef) && (
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
          {ef?.cue && <p className="text-center font-mono text-[11px] text-[#F5C518]">{ef.cue}</p>}
          {ef && (
            <button type="button" data-testid={`share-freeze-${proof.id}`} onClick={share} disabled={sharing}
              className="flex items-center gap-1.5 rounded-full border border-[#F5C518]/60 bg-black/60 hover:bg-[#F5C518] hover:text-black text-[#F5C518] px-3 py-1.5 font-mono text-[9px] tracking-[0.25em] uppercase transition-colors disabled:opacity-60"
              style={{ boxShadow: `0 0 14px ${GOLD}33` }}>
              {sharing ? <Loader2 size={12} className="animate-spin" /> : <Share2 size={12} />} {sharing ? "Building…" : "Share Freeze Frame"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ---- Full-speed source video with the adjustment marker ----
const SourceVideo = ({ proof, e, token, api, compact }) => {
  const videoRef = useRef(null);
  const [cur, setCur] = useState(0);
  const ef = e.error_frame;
  const showMark = ef && cur >= ef.t - 0.5 && cur <= ef.t + 2.6;
  const c = ef ? efCenter(ef) : null;
  return (
    <div className="space-y-2">
      {compact && (
        <div className="flex items-center gap-2">
          <Film size={13} className="text-white/50" />
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/50">Full-Speed Source</span>
        </div>
      )}
      <AnalysisStation engine={e} compact={compact} mode="Source Feed" testId={`analysis-frame-${proof.id}`}>
        <video ref={videoRef} data-testid={`engine-video-${proof.id}`} controls preload="metadata"
          className="w-full aspect-video bg-black block"
          src={`${api}/proofs/file/${proof.id}?auth=${token}`}
          onTimeUpdate={(ev) => setCur(ev.target.currentTime || 0)} />
        <AnimatePresence>
          {showMark && (
            <motion.div data-testid={`hud-pulse-${proof.id}`} className="absolute pointer-events-none z-20" style={{ left: `${c.x * 100}%`, top: `${c.y * 100}%` }}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
              <LockReticle label={`● Adjust${ef.joint ? ` · ${ef.joint}` : ""}`} pulse below={c.y < 0.18} />
            </motion.div>
          )}
        </AnimatePresence>
        {ef && (
          <button data-testid={`engine-jump-${proof.id}`} onClick={() => { if (videoRef.current) { videoRef.current.currentTime = Math.max(0, ef.t); videoRef.current.play?.().catch(() => {}); } }}
            className="absolute bottom-3 right-3 z-30 font-mono text-[10px] tracking-widest uppercase bg-[#C41E24] hover:bg-[#A0161D] text-white px-3 py-1.5 rounded-full flex items-center gap-1.5">
            <Target size={12} /> Show adjustment · {fmtTime(ef.t)}
          </button>
        )}
      </AnalysisStation>
    </div>
  );
};

const Verdict = ({ proof, e, onCueTap }) => {
  const ef = e.error_frame;
  return (
    <div data-testid={`engine-verdict-${proof.id}`} className="rounded-xl border border-[#C41E24]/40 bg-[#C41E24]/[0.06] p-4">
      <div className="flex items-center gap-2 mb-2">
        <Zap size={13} className="text-[#C41E24]" />
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/70">Next O Soccer Engine</span>
      </div>
      <p className="text-sm text-white/85 leading-relaxed">{e.verdict}</p>
      {(e.strengths?.length > 0 || e.focus?.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          {e.strengths?.length > 0 && (
            <div>
              <p className="font-mono text-[9px] tracking-widest uppercase text-[#22C55E] mb-1.5">Strengths</p>
              <ul className="space-y-1">{e.strengths.map((s, i) => <li key={i} className="text-xs text-white/70 flex gap-1.5"><span className="text-[#22C55E]">▸</span>{s}</li>)}</ul>
            </div>
          )}
          {e.focus?.length > 0 && (
            <div>
              <p className="font-mono text-[9px] tracking-widest uppercase text-[#F5C518] mb-1.5">Elite Adjustments{onCueTap && " · tap to voice"}</p>
              <ul className="space-y-1">{e.focus.map((s, i) => onCueTap ? (
                <li key={i}><button data-testid={`cue-${proof.id}-${i}`} onClick={() => onCueTap(s)} className="group text-left w-full text-xs text-white/80 hover:text-white flex items-start gap-1.5"><span className="text-[#F5C518]">▸</span><span className="group-hover:underline">{s}</span><Mic size={11} className="text-[#C41E24] opacity-40 group-hover:opacity-100 mt-0.5 flex-shrink-0" /></button></li>
              ) : (
                <li key={i} className="text-xs text-white/70 flex gap-1.5"><span className="text-[#F5C518]">▸</span>{s}</li>
              ))}</ul>
            </div>
          )}
        </div>
      )}
      {ef?.cue && (onCueTap ? (
        <button data-testid={`cue-error-${proof.id}`} onClick={() => onCueTap(ef.cue)} className="mt-3 font-mono text-[11px] text-[#F5C518] flex items-center gap-1.5 hover:underline"><Mic size={12} /> Voice this adjustment: {ef.cue}</button>
      ) : (
        <p className="mt-3 font-mono text-[11px] text-[#F5C518] flex items-center gap-1.5"><Zap size={12} /> Elite Adjustment: {ef.cue}</p>
      ))}
    </div>
  );
};

// ---- Full technical report ----
export const EngineReport = ({ proof, history = [], showVideo = true, token, api, onCueTap, player }) => {
  const e = proof.engine;
  const ready = !!e;
  // Cinematic "System Startup" hum each time a report comes on screen.
  useEffect(() => { if (ready) playStartup(); }, [proof.id, ready]);
  if (proof.engine_status === "scanning" || (!e && proof.engine_status !== "failed")) {
    return <EliteScan label={proof.move_label} />;
  }
  if (!e) {
    return <div className="rounded-lg border border-white/10 bg-black/40 p-6 text-center font-mono text-xs tracking-widest uppercase text-white/40">The Engine couldn't complete this scan. Try re-uploading a clearer clip.</div>;
  }
  const hasFix = !!(e.has_fix_clip || proof.fix_clip_path);
  const composite = (p) => Math.round(((p.engine.balance + p.engine.execution + p.engine.pace) / 3) * 10) / 10;
  const trend = [...history]
    .filter((p) => p.engine && ["balance", "execution", "pace"].every((k) => typeof p.engine[k] === "number"))
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    .map((p, i) => ({ n: i + 1, score: composite(p), date: new Date(p.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }), current: p.id === proof.id }));

  return (
    <div data-testid={`engine-report-${proof.id}`} className="space-y-5 font-sans">
      <div className="flex items-center gap-2">
        <Activity size={16} className="text-[#F5C518]" />
        <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#F5C518]">Technical Performance Engine</span>
        {e.elite && <span className="ml-auto font-mono text-[9px] tracking-widest uppercase text-black bg-[#F5C518] rounded-full px-2.5 py-1 flex items-center gap-1"><Crown size={11} /> Elite Mastered</span>}
      </div>
      <ElitePlayerCard player={player} engine={e} drill={proof.move_label} api={api} />
      {hasFix && <FreezeStation proof={proof} e={e} token={token} api={api} player={player} />}
      {showVideo && <SourceVideo proof={proof} e={e} token={token} api={api} compact={hasFix} />}
      <CoachVoiceBrief proof={proof} api={api} token={token} />
      <ProRadar engine={e} />
      <TechnicalMarkers proof={proof} markers={e.markers} />
      <ProTrend trend={trend} label={proof.move_label} />
      <Verdict proof={proof} e={e} onCueTap={onCueTap} />
    </div>
  );
};

// ---- Modal wrapper for the player Hub ----
export const EngineReportModal = ({ proof, history, token, api, onClose, player }) => (
  <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-2 sm:p-4" onClick={onClose} data-testid="engine-report-modal">
    <div className="relative bg-[#0b0b0c] border border-[#D4AF37]/25 rounded-2xl max-w-2xl w-full my-3 sm:my-8 p-4 sm:p-6" style={{ boxShadow: "0 0 60px rgba(196,30,36,0.18)" }} onClick={(ev) => ev.stopPropagation()}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="font-headings text-xl uppercase tracking-wide text-white">Mission Control</h3>
        <div className="flex items-center gap-3">
          <SoundToggle large />
          <button data-testid="engine-report-close" onClick={onClose} aria-label="Close" className="text-white/50 hover:text-white p-2 -m-2"><X size={20} /></button>
        </div>
      </div>
      <EngineReport proof={proof} history={history} token={token} api={api} showVideo player={player} />
    </div>
  </div>
);

export default EngineReport;
